/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef, useMemo } from "react";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { Skeleton } from "primereact/skeleton";
import { Divider } from "primereact/divider";
import {
  Boton,
  Titulo,
  Formulario,
  Sidebar,
  accessAPI,
  tituloBase,
  downloadFile,
  VistaCumplimiento,
  ArchivoCumplimiento,
  DocumentoEvidencia,
  ESTADOS_CUMPLIMIENTO as ESTADOS,
  estadoDe,
  vinculadas,
  sugerencias,
  etiquetaDocumento,
} from "../../shared";

const COBERTURAS = {
  TOTAL: { etiqueta: "Total", severidad: "success" },
  PARCIAL: { etiqueta: "Parcial", severidad: "warning" },
};

// Cada cuánto se consulta el avance de la revisión de documentos.
const MS_POLLING_REVISION = 5000;

// Los archivos de los requisitos se descargan por el endpoint del admin.
const rutaDescargaArchivo = (archivo) => `admin/archivo/${archivo.id}`;

/**
 * Cumplimiento del organismo con su norma de referencia. El organismo declara
 * el estado de cada requisito; lo que cubre la plataforma se muestra como
 * heredado, pero igual hay que confirmarlo.
 */
export default function Cumplimiento() {
  const [loader, setLoader] = useState(true);
  const [datos, setDatos] = useState(null);

  const [requisitoEditando, setRequisitoEditando] = useState(null);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [formEditarKey, setFormEditarKey] = useState(0);
  // Valores con los que "Aceptar sugerencia" completa el formulario. Null =
  // el formulario muestra lo que el requisito tiene guardado.
  const [valoresSugeridos, setValoresSugeridos] = useState(null);
  const [descartando, setDescartando] = useState(false);

  // Documentos que se pueden vincular. Se piden la primera vez que se abre el
  // panel.
  const [documentos, setDocumentos] = useState(null);

  // Revisión de todos los documentos con el asistente.
  const [revision, setRevision] = useState(null);
  const [lanzandoRevision, setLanzandoRevision] = useState(false);
  const [exportando, setExportando] = useState(false);

  const toast = useRef(null);

  useEffect(() => {
    document.title = "Cumplimiento";
    cargar();
    consultarRevision();
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mientras la revisión está en curso se consulta su avance; al terminar se
  // recargan los datos para ver las sugerencias.
  const enCurso = revision?.estado === "procesando";
  useEffect(() => {
    if (!enCurso) return;
    const intervalo = setInterval(
      () =>
        consultarRevision((nueva) => {
          if (nueva?.estado !== "procesando") cargar();
        }),
      MS_POLLING_REVISION,
    );
    return () => clearInterval(intervalo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enCurso]);

  function cargar() {
    accessAPI(
      "GET",
      "admin/cumplimiento",
      null,
      (respuesta) => {
        setDatos(respuesta);
        setLoader(false);
      },
      (respuesta) => {
        setLoader(false);
        mostrarError(respuesta, "No se pudo cargar el cumplimiento");
      },
    );
  }

  function consultarRevision(alTerminar) {
    accessAPI(
      "GET",
      "admin/cumplimiento/revision",
      null,
      (respuesta) => {
        setRevision(respuesta);
        alTerminar?.(respuesta);
      },
      () => {},
    );
  }

  // "Cumplimiento 17065 - 2026-09-30.xlsx". Del código de la norma queda solo
  // el número ("ISO/IEC 17021-1:2015" → "17021-1"): / y : no van en un nombre
  // de archivo. Con varias normas van todos los números.
  function nombreExportacion() {
    const numeros = datos.normas
      .map((n) => n.codigo.match(/\d{5}(-\d+)?/)?.[0] || n.codigo)
      .join(" ");
    const hoy = new Date();
    const dos = (n) => String(n).padStart(2, "0");
    const fecha = `${hoy.getFullYear()}-${dos(hoy.getMonth() + 1)}-${dos(hoy.getDate())}`;
    return `Cumplimiento ${numeros} - ${fecha}.xlsx`;
  }

  function exportar() {
    setExportando(true);
    downloadFile(
      "admin/cumplimiento/exportar",
      nombreExportacion(),
      () => setExportando(false),
      (respuesta) => {
        setExportando(false);
        mostrarError(respuesta, "No se pudo exportar el cumplimiento");
      },
    );
  }

  function lanzarRevision(norma) {
    setLanzandoRevision(true);
    accessAPI(
      "POST",
      "admin/cumplimiento/revision",
      { norma: norma.codigo },
      (respuesta) => {
        setLanzandoRevision(false);
        setRevision(respuesta);
      },
      (respuesta) => {
        setLanzandoRevision(false);
        mostrarError(respuesta, "No se pudo iniciar la revisión");
      },
    );
  }

  function mostrarError(respuesta, mensajePorDefecto) {
    toast.current?.show({
      severity: "error",
      summary: "Error",
      detail: respuesta?.[0]?.msg || mensajePorDefecto,
      sticky: true,
    });
  }

  // Aplica un cambio a un requisito dentro de los datos cargados.
  function actualizarRequisito(id, cambio) {
    setDatos((d) => ({
      ...d,
      normas: d.normas.map((n) => ({
        ...n,
        apartados: n.apartados.map((a) => ({
          ...a,
          requisitos: a.requisitos.map((r) => (r.id === id ? cambio(r) : r)),
        })),
      })),
    }));
  }

  function abrirPanel(requisito) {
    setValoresSugeridos(null);
    setFormEditarKey((k) => k + 1);
    setRequisitoEditando(requisito);
    if (documentos === null) {
      accessAPI(
        "GET",
        "admin/cumplimiento/documentos",
        null,
        (respuesta) => setDocumentos(respuesta),
        (respuesta) => {
          setDocumentos([]);
          mostrarError(respuesta, "No se pudieron cargar los documentos");
        },
      );
    }
  }

  // Con archivo, accessAPI manda multipart: cada campo viaja como texto, así
  // que la lista de documentos va como JSON y el comentario vacío como "".
  function guardar(campos, archivo) {
    enviarCumplimiento(
      archivo
        ? {
            estado: campos.estado,
            comentario: campos.comentario || "",
            documentoIds: JSON.stringify(campos.documentoIds || []),
          }
        : {
            estado: campos.estado,
            comentario: campos.comentario || null,
            documentoIds: campos.documentoIds || [],
            quitarArchivo: campos.quitarArchivo === true,
          },
      archivo,
    );
  }

  // Completa el formulario con las sugerencias del asistente, sin guardar: el
  // usuario revisa, ajusta y guarda. Queda "Cumple", con la justificación del
  // asistente en "Cómo se cumple" y los documentos sugeridos como respaldo. Se
  // conservan el comentario y los documentos confirmados que ya tenía; los "a
  // revisar" (versiones que ya no lo cubren) se sacan de la lista.
  function aceptarSugerencia() {
    const r = requisitoEditando;
    const sugeridas = sugerencias(r);
    const comentarioAsistente = sugeridas
      .map((e) => e.justificacion)
      .filter(Boolean)
      .join("\n");
    setValoresSugeridos({
      estado: "CUMPLE",
      comentario: [r.cumplimiento?.comentario, comentarioAsistente]
        .filter(Boolean)
        .join("\n\n"),
      documentoIds: [
        ...new Set([
          ...r.evidencias
            .filter((e) => e.estado === "CONFIRMADA")
            .map((e) => e.documento.id),
          ...sugeridas.map((e) => e.documento.id),
        ]),
      ],
    });
    // Formulario solo toma los valores iniciales al montarse.
    setFormEditarKey((k) => k + 1);
  }

  // Descarta las sugerencias del asistente (quedan rechazadas y no se vuelven
  // a proponer) y deja el formulario con lo que el requisito tiene guardado,
  // para que el usuario lo complete como quiera.
  function descartarSugerencias() {
    const id = requisitoEditando.id;
    setDescartando(true);
    accessAPI(
      "POST",
      `admin/cumplimiento/requisito/${id}/descartar`,
      null,
      ({ evidencias }) => {
        setDescartando(false);
        actualizarRequisito(id, (r) => ({ ...r, evidencias }));
        setRequisitoEditando((r) => ({ ...r, evidencias }));
        setValoresSugeridos(null);
        setFormEditarKey((k) => k + 1);
      },
      (respuesta) => {
        setDescartando(false);
        mostrarError(respuesta, "No se pudieron descartar las sugerencias");
      },
    );
  }

  function enviarCumplimiento(payload, archivo) {
    const id = requisitoEditando.id;
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/cumplimiento/requisito/${id}`,
      payload,
      ({ cumplimiento, evidencias }) => {
        setLoaderEditar(false);
        actualizarRequisito(id, (r) => ({ ...r, cumplimiento, evidencias }));
        setRequisitoEditando(null);
        toast.current?.show({
          severity: "success",
          summary: "Requisito actualizado",
          life: 3000,
        });
      },
      (respuesta) => {
        setLoaderEditar(false);
        mostrarError(respuesta, "No se pudo guardar el requisito");
      },
      archivo,
    );
  }

  function cambiarOpcion(opcion) {
    accessAPI(
      "PUT",
      "admin/cumplimiento/opcion",
      { opcion },
      () => setDatos((d) => ({ ...d, opcionSistemaGestion: opcion })),
      (respuesta) => mostrarError(respuesta, "No se pudo cambiar la opción"),
    );
  }

  // Se memoriza: Formulario se reinicializa cada vez que recibe una config
  // nueva, y sin esto cualquier render (como el del loader al guardar) borraría
  // lo que el usuario escribió.
  const configEditar = useMemo(() => {
    if (!requisitoEditando || documentos === null) return null;

    // Los documentos ya vinculados se ofrecen aunque hayan dejado de estar
    // disponibles, para que se vean y se puedan quitar.
    const vinculados = vinculadas(requisitoEditando);
    const opciones = [
      ...documentos.map((d) => ({
        value: d.id,
        etiqueta: etiquetaDocumento(d, d.version),
      })),
      ...vinculados
        .filter((e) => !documentos.some((d) => d.id === e.documento.id))
        .map((e) => ({
          value: e.documento.id,
          etiqueta: etiquetaDocumento(e.documento, e.version),
        })),
    ];

    return {
      secciones: [
        {
          campos: [
            {
              nombre: "estado",
              tipoitemformularioid: 5,
              etiqueta: "Estado",
              sinOpcionInicial: true,
              requerido: true,
              opciones: Object.entries(ESTADOS).map(([value, e]) => ({
                value,
                etiqueta: e.etiqueta,
              })),
              respuesta:
                valoresSugeridos?.estado ?? estadoDe(requisitoEditando),
            },
            {
              nombre: "comentario",
              tipoitemformularioid: 11,
              etiqueta: "Cómo se cumple",
              respuesta:
                valoresSugeridos?.comentario ??
                requisitoEditando.cumplimiento?.comentario,
            },
            {
              nombre: "documentoIds",
              tipoitemformularioid: 12,
              etiqueta: "Documentos que lo respaldan",
              opciones,
              respuesta:
                valoresSugeridos?.documentoIds ??
                vinculados.map((e) => e.documento.id),
            },
            {
              nombre: "archivo",
              tipoitemformularioid: 10,
              etiqueta: requisitoEditando.cumplimiento?.archivo
                ? "Reemplazar el archivo"
                : "Archivo que lo demuestra",
            },
            ...(requisitoEditando.cumplimiento?.archivo
              ? [
                  {
                    nombre: "quitarArchivo",
                    tipoitemformularioid: 6,
                    etiqueta: "Quitar el archivo actual",
                  },
                ]
              : []),
          ],
        },
      ],
      botones: [{ etiqueta: "Guardar", callback: guardar, validar: true }],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requisitoEditando, documentos, formEditarKey, valoresSugeridos]);

  if (loader) {
    return (
      <>
        <Toast ref={toast} position="top-center" />
        <Skeleton width="20rem" height="2rem" css={{ marginBottom: "1em" }} />
        <Card>
          <Skeleton width="100%" height="20rem" />
        </Card>
      </>
    );
  }

  if (!datos) return <Toast ref={toast} position="top-center" />;

  const botonExportar = (
    <Boton transparente callback={exportar} loader={exportando}>
      Exportar
    </Boton>
  );

  // Cada norma se revisa por separado, de a una por vez: mientras corre la
  // revisión de una, el botón de las otras queda deshabilitado.
  const botonRevision = (norma) =>
    enCurso && revision.norma === norma.codigo ? (
      <span css={{ color: "var(--grisTextos)" }}>
        <i className="pi pi-spin pi-spinner" css={{ marginRight: "0.5rem" }} />
        Revisando documentos: {revision.procesados} de {revision.total}
      </span>
    ) : (
      <Boton
        transparente
        disabled={enCurso}
        callback={() => lanzarRevision(norma)}
        loader={lanzandoRevision}
        confirmacion={`El asistente va a revisar todos los documentos contra la ${norma.codigo} y sugerir qué requisitos cubre cada uno. Se saltean los requisitos ya declarados con documentos que siguen en su versión vigente. ¿Continuar?`}
      >
        Revisar documentos
      </Boton>
    );

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Sidebar
        visible={!!requisitoEditando}
        onHide={() => setRequisitoEditando(null)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Requisito {requisitoEditando?.numero}</Titulo>
        <p css={{ marginTop: 0 }}>{requisitoEditando?.texto}</p>
        {requisitoEditando?.plataforma && (
          <p css={{ color: "var(--grisTextos)", whiteSpace: "pre-wrap" }}>
            <strong>Plataforma:</strong> {requisitoEditando.plataforma}
          </p>
        )}
        {requisitoEditando &&
          [
            ...vinculadas(requisitoEditando).filter(
              (e) => e.estado === "REVISAR",
            ),
            ...sugerencias(requisitoEditando),
          ].length > 0 && (
            <>
              <Titulo>Asistente</Titulo>
              {[
                ...vinculadas(requisitoEditando).filter(
                  (e) => e.estado === "REVISAR",
                ),
                ...sugerencias(requisitoEditando),
              ].map((e) => (
                <div
                  key={e.id}
                  css={{
                    padding: "0.5rem 0",
                    borderBottom: "1px solid var(--surface-border, #dee2e6)",
                  }}
                >
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <strong>
                      <DocumentoEvidencia
                        evidencia={e}
                        etiqueta={etiquetaDocumento(e.documento, e.version)}
                      />
                    </strong>
                    {e.estado === "REVISAR" ? (
                      <Tag value="Ya no lo cubre" severity="danger" />
                    ) : (
                      <Tag
                        value={`Sugerido - ${COBERTURAS[e.cobertura]?.etiqueta}`}
                        severity={COBERTURAS[e.cobertura]?.severidad}
                      />
                    )}
                  </div>
                  {e.estado === "PROPUESTA" && e.cita && (
                    <div
                      css={{
                        marginTop: "0.25rem",
                        fontStyle: "italic",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      "{e.cita}"
                    </div>
                  )}
                  {e.estado === "PROPUESTA" && e.justificacion && (
                    <div
                      css={{
                        marginTop: "0.25rem",
                        color: "var(--grisTextos)",
                      }}
                    >
                      {e.justificacion}
                    </div>
                  )}
                </div>
              ))}
              {sugerencias(requisitoEditando).length > 0 && (
                <div
                  css={{ marginTop: "1rem", display: "flex", gap: "0.5rem" }}
                >
                  <Boton transparente callback={aceptarSugerencia}>
                    Aceptar sugerencia
                  </Boton>
                  <Boton
                    transparente
                    rojo
                    callback={descartarSugerencias}
                    loader={descartando}
                  >
                    Descartar sugerencia
                  </Boton>
                </div>
              )}
            </>
          )}
        <Divider />
        {requisitoEditando?.cumplimiento?.archivo && (
          <p>
            <strong>Archivo actual:</strong>{" "}
            <ArchivoCumplimiento
              archivo={requisitoEditando.cumplimiento.archivo}
              toast={toast}
              rutaDescarga={rutaDescargaArchivo}
            />
          </p>
        )}
        {configEditar ? (
          <Formulario
            key={formEditarKey}
            config={configEditar}
            loader={loaderEditar}
          />
        ) : (
          <Skeleton width="100%" height="16rem" />
        )}
      </Sidebar>

      <VistaCumplimiento
        datos={datos}
        toast={toast}
        rutaDescargaArchivo={rutaDescargaArchivo}
        onEditar={abrirPanel}
        onCambiarOpcion={cambiarOpcion}
        accionesNorma={botonRevision}
        botonesTitulo={(norma) =>
          norma ? (
            <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              {botonRevision(norma)}
              {botonExportar}
            </div>
          ) : (
            botonExportar
          )
        }
      />
    </>
  );
}
